import { useState, useEffect, useCallback, useRef } from "react";

const WS_RECONNECT_DELAY = 3000; // Reconnect delay
const WS_PING_INTERVAL = 30000;
const POLLING_INTERVAL = 30000; // Fallback polling interval (30s to reduce server load)
const WS_CONNECTION_TIMEOUT = 5000; // 5 seconds timeout for WS connection
const MAX_WS_FAIL_COUNT = 3; // Switch to polling after 3 failed attempts

/**
 * WebSocket hook for real-time sync updates with polling fallback
 * @param {string} clientId - Unique client identifier
 * @param {Function} onMessage - Callback for received messages
 * @param {boolean} autoConnect - Auto connect on mount (default: true)
 */
export const useWebSocket = (clientId, onMessage, autoConnect = true) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [usePolling, setUsePolling] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const connectRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  const lastSyncStatusRef = useRef(null);
  const wsFailCountRef = useRef(0);

  // Keep onMessage ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Build WebSocket URL from the current browser origin
  const getWsUrl = useCallback(() => {
    const backendUrl = window.location.origin;
    // Convert http(s) to ws(s)
    const wsProtocol = backendUrl.startsWith("https") ? "wss" : "ws";
    const host = backendUrl.replace(/^https?:\/\//, "");
    return `${wsProtocol}://${host}/ws/${clientId}`;
  }, [clientId]);

  const cleanup = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // Polling fallback - check sync status via REST API
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return;
    
    setUsePolling(true);
    setConnectionStatus("polling");
    console.log("[WebSocket] Starting polling fallback...");
    
    const poll = async () => {
      try {
        const backendUrl = window.location.origin;
        const response = await fetch(`${backendUrl}/api/sync/status`);
        
        if (response.ok) {
          const data = await response.json();
          
          // Only emit events when sync state actually changes
          const prevStatus = lastSyncStatusRef.current;
          const currentIsSyncing = data.is_syncing;
          const currentPlatform = data.platform;
          const currentLastSync = data.last_sync;
          
          // Detect state transitions
          const wasSyncing = prevStatus?.is_syncing;
          const prevLastSync = prevStatus?.last_sync;
          
          // Update ref with current state
          lastSyncStatusRef.current = {
            is_syncing: currentIsSyncing,
            platform: currentPlatform,
            last_sync: currentLastSync,
            posts_synced: data.posts_synced
          };
          
          // Emit sync_progress only when actively syncing
          if (currentIsSyncing) {
            onMessageRef.current?.({
              type: "sync_progress",
              platform: currentPlatform || "unknown",
              message: data.message || "Syncing...",
              timestamp: new Date().toISOString()
            });
          } 
          // Emit sync_completed only on state transition: was syncing -> now not syncing
          // OR when last_sync timestamp changes (new sync completed)
          else if (wasSyncing && !currentIsSyncing) {
            onMessageRef.current?.({
              type: "sync_completed",
              platform: currentPlatform || "batch",
              posts_synced: data.posts_synced || 0,
              timestamp: currentLastSync
            });
          }
          // Don't emit sync_completed repeatedly for the same completed state
        }
      } catch (e) {
        // Silently fail - polling is a fallback
      }
    };
    
    // Initial poll
    poll();
    
    // Start polling interval
    pollingIntervalRef.current = setInterval(poll, POLLING_INTERVAL);
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    cleanup();
    setConnectionStatus("connecting");

    try {
      const wsUrl = getWsUrl();
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Set connection timeout
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          wsFailCountRef.current += 1;
          
          // After MAX_WS_FAIL_COUNT failed attempts, switch to polling directly
          if (wsFailCountRef.current >= MAX_WS_FAIL_COUNT) {
            startPolling();
          }
        }
      }, WS_CONNECTION_TIMEOUT);

      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        wsFailCountRef.current = 0;
        setIsConnected(true);
        setConnectionStatus("connected");
        setUsePolling(false);
        console.log("[WebSocket] Connected to", wsUrl);

        // Stop polling if it was running
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        // Start ping interval for keepalive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, WS_PING_INTERVAL);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
          
          // Skip pong messages
          if (data.type !== "pong") {
            onMessageRef.current?.(data);
          }
        } catch (e) {
          console.warn("[WebSocket] Failed to parse message:", event.data);
        }
      };

      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        wsFailCountRef.current += 1;
        // Don't set "error" status — let onclose handle the transition to polling
      };

      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        setIsConnected(false);

        // Auto reconnect if not intentionally closed
        if (shouldReconnectRef.current && event.code !== 1000) {
          // After MAX_WS_FAIL_COUNT failures, switch to polling directly
          if (wsFailCountRef.current >= MAX_WS_FAIL_COUNT) {
            startPolling();
          } else {
            setConnectionStatus("reconnecting");
            reconnectTimeoutRef.current = setTimeout(() => {
              connectRef.current?.();
            }, WS_RECONNECT_DELAY);
          }
        } else {
          setConnectionStatus("disconnected");
        }
      };
    } catch (error) {
      wsFailCountRef.current += 1;
      
      if (wsFailCountRef.current >= MAX_WS_FAIL_COUNT) {
        startPolling();
      }
    }
  }, [getWsUrl, cleanup, startPolling]);

  // Store connect in ref for recursive reconnect
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    cleanup();
    setIsConnected(false);
    setUsePolling(false);
    setConnectionStatus("disconnected");
  }, [cleanup]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const payload = typeof message === "string" ? message : JSON.stringify(message);
      wsRef.current.send(payload);
      return true;
    }
    return false;
  }, []);

  // Auto connect on mount
  useEffect(() => {
    if (autoConnect) {
      shouldReconnectRef.current = true;
      wsFailCountRef.current = 0;
      connect();
    }

    return () => {
      shouldReconnectRef.current = false;
      cleanup();
    };
  }, [autoConnect, connect, cleanup]);

  return {
    isConnected,
    connectionStatus,
    lastMessage,
    usePolling,
    connect,
    disconnect,
    sendMessage,
  };
};

export default useWebSocket;
